/**
 * DatePicker Component - Foundational Primitive #9 (DateSelector)
 *
 * Styled date picker with calendar popup. Uses CSS primitive classes from:
 * - 08-form-elements.css for .form-date-selector styling
 *
 * Features:
 * - Gold border (per DO THIS/NOT THAT guidelines)
 * - Playfair Display font for date values
 * - Sharp corners (border-radius: 0)
 * - Gold focus ring
 * - Locale-aware date formatting (18+ locales)
 * - Theme-based calendar styling
 */

import React, { forwardRef, useMemo, useCallback, memo } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useDatePickerLocale } from '../../hooks/useDatePickerLocale';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/datepicker.css';
import '../../theme-components/08-form-elements.css';

export interface DatePickerProps {
  /** ISO date string (YYYY-MM-DD) */
  value?: string;
  /** Callback fired when date changes - returns ISO date string */
  onChange: (date: string) => void;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Input placeholder text */
  placeholder?: string;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Whether input is required */
  required?: boolean;
  /** Locale for date formatting (falls back to user's detected locale) */
  locale?: string;
  /** Whether input has validation error */
  error?: boolean;
  /** Additional CSS classes for input */
  className?: string;
  /** Additional inline styles for input */
  style?: React.CSSProperties;
  /** Focus event handler */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Blur event handler */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Test ID for testing */
  'data-testid'?: string;
}

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  className?: string;
  style?: React.CSSProperties;
  'data-testid'?: string;
}

// Memoized custom input component that matches native date input styling
const CustomInput = memo(forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => {
  const { theme } = useTheme();
  const { 
    error, 
    onFocus, 
    onBlur, 
    onClick, 
    className,
    style,
    'data-testid': testId,
    ...inputProps 
  } = props;

  // Memoize the computed className to prevent unnecessary re-renders
  const inputClassName = useMemo(() => {
    const baseClasses = [
      'form-date-selector',  // CSS Primitive from 08-form-elements.css
      'w-full p-4 pr-12',
      'border-2 sharp-corners',
      'text-base transition-all duration-200 ease-in-out',
      'cursor-pointer outline-none',
      'focus:outline-none',
      'appearance-none',
      'custom-date-input'
    ];

    // Use Tailwind status-error class instead of hardcoded color
    if (error) {
      baseClasses.push('border-status-error');
    }

    if (className) {
      baseClasses.push(className);
    }

    return baseClasses.join(' ');
  }, [error, className]);

  // Memoize the inline styles to prevent object recreation
  const inputStyle = useMemo(() => ({
    // Removed hardcoded borderColor - now using Tailwind class
    backgroundColor: theme.userInterface.inputBox.inactive.background,
    color: theme.brand.colors.text.primary,
    fontFamily: theme.brand.fonts.primary,
    ...style
  }), [theme, style]);

  const iconStyle = useMemo(() => ({
    color: theme.brand.colors.primary
  }), [theme.brand.colors.primary]);

  // Memoized focus handler
  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = theme.brand.colors.primary;
    e.target.style.backgroundColor = theme.userInterface.inputBox.active.background;
    onFocus?.(e);
  }, [theme, onFocus]);

  // Memoized blur handler  
  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (!error) {
      e.target.style.borderColor = theme.userInterface.inputBox.inactive.border;
      e.target.style.backgroundColor = theme.userInterface.inputBox.inactive.background;
    }
    onBlur?.(e);
  }, [error, theme, onBlur]);

  return (
    <div 
      className="relative w-full cursor-pointer"
      onClick={onClick}
    >
      <input
        {...inputProps}
        ref={ref}
        readOnly
        className={inputClassName}
        style={inputStyle}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={testId}
      />
      {/* Calendar Icon */}
      <div
        className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
        style={iconStyle}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="0" ry="0" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>
    </div>
  );
}));

CustomInput.displayName = 'CustomInput';

/**
 * Optimized DatePicker component with locale support and theme integration
 * 
 * Features:
 * - Locale-aware date formatting (18+ locales)
 * - Theme-based styling with CSS custom properties
 * - Memoized components and callbacks for performance
 * - Accessible with ARIA support
 * - TypeScript typed with comprehensive prop validation
 */
const DatePicker: React.FC<DatePickerProps> = memo(({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder,
  disabled = false,
  required = false,
  locale,
  error = false,
  className,
  style,
  onFocus,
  onBlur,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Use translation for default placeholder
  const defaultPlaceholder = t('datePicker.selectDate');
  
  // Use custom hook for locale logic
  const {
    dateLocale,
    dateFormat,
    formatDisplayValue,
    parseValue,
    formatValue,
  } = useDatePickerLocale(locale);

  // Memoize selected date parsing
  const selectedDate = useMemo(() => parseValue(value), [parseValue, value]);

  // Memoized date change handler
  const handleDateChange = useCallback((date: Date | null) => {
    const isoString = formatValue(date);
    onChange(isoString);
  }, [formatValue, onChange]);

  // Memoized display value
  const displayValue = useMemo(() => formatDisplayValue(selectedDate), [formatDisplayValue, selectedDate]);

  // Memoize the theme-based styles to prevent recreation
  const themeStyles = useMemo(() => ({
    '--datepicker-font-family': theme.brand.fonts.primary,
    '--datepicker-border-color': theme.userInterface.inputBox.inactive.border,
    '--datepicker-background': theme.userInterface.inputBox.inactive.background,
    '--datepicker-primary-color': theme.brand.colors.primary,
    '--datepicker-secondary-color': theme.brand.colors.secondary,
    '--datepicker-text-primary': theme.brand.colors.text.primary,
    '--datepicker-text-secondary': theme.brand.colors.text.secondary,
  } as React.CSSProperties), [theme]);

  // Memoize the custom input component
  const customInput = useMemo(() => (
    <CustomInput
      error={error}
      style={style}
      onFocus={onFocus}
      onBlur={onBlur}
      value={displayValue}
      className={className}
      data-testid={testId}
    />
  ), [error, style, onFocus, onBlur, displayValue, className, testId]);

  return (
    <div 
      className="w-full"
      style={themeStyles}
    >
      {/* Dynamic CSS for theme-based styling using Theme v2.0 calendar variables */}
      <style>{`
        /* Custom date input styling with Theme v2.0 */
        .custom-date-input {
          border-color: var(--calendar-container-border);
          background-color: var(--calendar-container-bg);
          color: var(--calendar-day-color);
          font-family: var(--calendar-day-font);
        }

        .custom-date-input:focus {
          border-color: var(--calendar-day-selected-border) !important;
        }

        .custom-date-input:hover {
          border-color: var(--calendar-day-hover-bg);
        }

        /* Calendar popup theme styling */
        .react-datepicker {
          font-family: var(--calendar-day-font);
          border-color: var(--calendar-container-border);
          background-color: var(--calendar-container-bg);
          border-radius: var(--calendar-container-border-radius);
          box-shadow: var(--calendar-container-shadow);
        }

        .react-datepicker__header {
          background-color: var(--calendar-container-bg);
          border-bottom: 1px solid var(--calendar-container-border);
        }

        .react-datepicker__month-container {
          background-color: var(--calendar-container-bg);
        }

        .react-datepicker__current-month {
          font-family: var(--calendar-month-font);
          font-size: var(--calendar-month-font-size);
          font-weight: var(--calendar-month-font-weight);
          color: var(--calendar-month-color);
        }

        .react-datepicker__day-name {
          font-family: var(--calendar-day-header-font);
          font-size: var(--calendar-day-header-font-size);
          font-weight: var(--calendar-day-header-font-weight);
          color: var(--calendar-day-header-color);
          text-transform: var(--calendar-day-header-text-transform);
          letter-spacing: var(--calendar-day-header-letter-spacing);
        }

        .react-datepicker__day {
          font-family: var(--calendar-day-font);
          font-size: var(--calendar-day-font-size);
          font-weight: var(--calendar-day-font-weight);
          color: var(--calendar-day-color);
          background-color: var(--calendar-day-bg);
        }

        .react-datepicker__day:hover {
          background-color: var(--calendar-day-hover-bg);
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: var(--calendar-day-selected-bg);
          color: var(--calendar-day-selected-color) !important;
          font-weight: var(--calendar-day-selected-font-weight);
          border: var(--calendar-day-selected-border);
        }

        .react-datepicker__day--selected:hover {
          background-color: var(--calendar-day-selected-hover-bg);
        }

        .react-datepicker__day--today {
          background-color: var(--calendar-day-bg);
          border: 2px solid var(--calendar-day-today-indicator-color);
        }

        .react-datepicker__day--outside-month {
          color: var(--calendar-day-disabled-color);
          opacity: 0.5;
        }

        .react-datepicker__day--disabled {
          color: var(--calendar-day-disabled-color) !important;
          background-color: var(--calendar-day-disabled-bg) !important;
          cursor: var(--calendar-day-disabled-cursor) !important;
          opacity: 0.5 !important;
        }

        .react-datepicker__day--disabled:hover {
          background-color: var(--calendar-day-disabled-bg) !important;
        }

        .react-datepicker__year-dropdown,
        .react-datepicker__month-dropdown {
          background-color: var(--calendar-container-bg);
          border-color: var(--calendar-container-border);
        }

        .react-datepicker__year-option,
        .react-datepicker__month-option {
          color: var(--calendar-day-color);
        }

        .react-datepicker__year-option:hover,
        .react-datepicker__month-option:hover {
          background-color: var(--calendar-day-hover-bg);
        }

        .react-datepicker__year-option--selected,
        .react-datepicker__month-option--selected {
          background-color: var(--calendar-day-selected-bg);
          color: var(--calendar-day-selected-color);
        }

        .react-datepicker__navigation {
          color: var(--calendar-nav-color);
        }

        .react-datepicker__navigation:hover {
          color: var(--calendar-nav-hover-color);
        }

        /* Ensure calendar popup is above all other elements */
        .react-datepicker-popper {
          z-index: 9999 !important;
        }

        .custom-datepicker-popper {
          z-index: 9999 !important;
        }

        /* Prevent calendar from being clipped by parent containers */
        .react-datepicker__tab-loop {
          position: absolute;
        }
      `}</style>

      <ReactDatePicker
        selected={selectedDate}
        onChange={handleDateChange}
        minDate={minDate}
        maxDate={maxDate}
        locale={dateLocale}
        dateFormat={dateFormat}
        placeholderText={placeholder || defaultPlaceholder}
        disabled={disabled}
        required={required}
        customInput={customInput}
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        calendarClassName="custom-datepicker"
        popperClassName="custom-datepicker-popper"
        popperPlacement="bottom-start"
        wrapperClassName="w-full block"
        // Accessibility
        aria-label={placeholder || defaultPlaceholder}
        // Performance optimization
        shouldCloseOnSelect
        preventOpenOnFocus={disabled}
      />
    </div>
  );
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;